import classNames from 'classnames'
import type { FC } from 'react'
import React from 'react'

import { useTabsContext } from './tabs-context'
import type { TabScrollButtonProps } from './types'
import { TAB_THEME_KEY } from './types'
import type { Theme, ThemeUICSSObject } from '../../theme-provider/types'
import { transitions } from '../../themes/theme-keys'
import { EMPTY_OBJECT, omit, pick } from '../../utils/object'
import {
  getLayout,
  getPosition,
  omitLayout,
  omitPosition
} from '../../utils/props-utils'
import { FlexPrivate } from '../_primitives/flex/flex'
import { ButtonPrivate } from '../button/button'

const TabScrollButton: FC<TabScrollButtonProps> = React.forwardRef<
  HTMLButtonElement,
  TabScrollButtonProps & {
    theme?: Theme
  }
>(function TabScrollButton(props, ref) {
  const {
    variant = 'scrollButton',
    className,
    direction,
    orientation,
    disabled,
    enableTransition,
    sx = EMPTY_OBJECT as ThemeUICSSObject,
    ...rest
  } = props
  const { variant: tabVariant } = useTabsContext()

  // remove size from layout fields
  const combineLayout = omit(
    {
      ...(orientation === 'vertical' ? {} : { height: '100%' }), // default height for tabs button
      ...getLayout(rest),
      ...getLayout(sx)
    },
    ['size']
  )

  const combinedPosition = {
    ...getPosition(rest as unknown as ThemeUICSSObject),
    ...getPosition(sx)
  }

  return orientation === 'vertical' ? (
    <FlexPrivate
      className={classNames('csg-tui-tab-scroll-button-wrapper', {
        'is-left': direction === 'left',
        'is-right': direction !== 'left'
      })}
      width="100%"
      absolute
      justifyContent="center"
      __css={{
        left: combinedPosition.left ? combinedPosition.left : 0,
        right: combinedPosition.right ? combinedPosition.right : 0,
        '&.is-left': {
          top: combinedPosition.top ? combinedPosition.top : 0
        },
        '&.is-right': {
          bottom: combinedPosition.bottom ? combinedPosition.bottom : 0
        }
      }}
    >
      <ButtonPrivate
        ref={ref}
        variant={tabVariant ? `${tabVariant}.${variant}` : variant}
        __themeKey={TAB_THEME_KEY}
        className={classNames(
          {
            'is-disabled': disabled,
            'is-left': direction === 'left',
            'is-right': direction !== 'left'
          },
          className
        )}
        __css={{
          flexShrink: 0,
          transition: transitions.create('transform'),
          '&.is-right': {
            transform: 'translateY(70%)',
            borderTopLeftRadius: 'pill',
            borderTopRightRadius: 'pill'
          },
          '&.is-left': {
            transform: 'translateY(-70%)',
            borderBottomLeftRadius: 'pill',
            borderBottomRightRadius: 'pill'
          },
          '&:hover': { transform: 'translateY(0)' },
          '&.is-disabled': {
            opacity: 0
          },
          '& svg': {
            transform: 'rotate(90deg)'
          },
          ...combineLayout
        }}
        disabled={disabled}
        size="1em"
        icon={direction === 'left' ? 'chevronLeft' : 'chevronRight'}
        sx={omitPosition(omitLayout(sx))}
        {...{ ...omitPosition(omitLayout(rest)), ...pick(rest, ['size']) }}
      />
    </FlexPrivate>
  ) : (
    <ButtonPrivate
      ref={ref}
      variant={tabVariant ? `${tabVariant}.scrollButton` : variant}
      __themeKey={TAB_THEME_KEY}
      className={classNames(
        {
          'is-disabled': disabled,
          'is-left': direction === 'left',
          'is-right': direction !== 'left'
        },
        className
      )}
      __css={{
        flexShrink: 0,
        position: 'absolute',
        zIndex: 2,
        ...(direction === 'left' ? { justifyContent: 'flex-end' } : {}),
        ...(orientation === 'horizontal'
          ? {
              top: combinedPosition.top ? combinedPosition.top : '-1px',
              bottom: combinedPosition.bottom ? combinedPosition.bottom : 0
            }
          : {
              left: combinedPosition.left ? combinedPosition.left : 0,
              right: combinedPosition.right ? combinedPosition.right : 0
            }),
        ...(direction === 'left'
          ? {
              left: combinedPosition.left ? combinedPosition.left : 0
            }
          : {
              right: combinedPosition.right ? combinedPosition.right : 0
            }),
        ...(enableTransition
          ? {
              borderRadius: 'pill',
              transition: transitions.create('transform'),
              ...(direction === 'left'
                ? {
                    transform: 'translateX(-80%)',
                    '&:hover': { transform: 'translateX(-40%)' }
                  }
                : {
                    transform: 'translateX(80%)',
                    '&:hover': { transform: 'translateX(40%)' }
                  })
            }
          : {
              background:
                direction === 'left'
                  ? 'linear-gradient(270deg, rgba(255,255,255,0) 0%, #FFFFFF 100%)'
                  : 'linear-gradient(90deg, rgba(255,255,255,0) 0%, #FFFFFF 100%)'
            }),
        '&.is-disabled': {
          opacity: 0
        },
        ...combineLayout
      }}
      disabled={disabled}
      size="1em"
      icon={
        direction === 'left'
          ? 'mui-keyboardArrowLeft'
          : 'mui-keyboardArrowRight'
      }
      sx={omitPosition(omitLayout(sx))}
      {...{ ...omitPosition(omitLayout(rest)), ...pick(rest, ['size']) }}
    />
  )
})

export default TabScrollButton
